import { forwardRef, type ComponentProps } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/utils";
import { iconMap, type IconName } from "./icons";

const iconVariants = cva("shrink-0", {
  variants: {
    size: {
      xs: "h-6 w-6",
      sm: "h-8 w-8",
      md: "h-10 w-10",
      lg: "h-12 w-12",
      xl: "h-16 w-16",
      "2xl": "h-20 w-20",
    },
    color: {
      current: "text-current",
      default: "text-on-surface-default",
      neutral: "text-on-surface-inverse",
      primary: "text-surface-primary-default",
      secondary: "text-on-surface-variant_5",
      success: "text-semantic-success",
      warning: "text-semantic-warning",
      info: "text-semantic-info",
      error: "text-semantic-error",
    },
  },
  defaultVariants: {
    size: "md",
    color: "default",
  },
});

type IconProps = Omit<ComponentProps<"svg">, "width" | "height" | "color"> &
  VariantProps<typeof iconVariants> & {
    name: IconName;
    width?: number | string;
    height?: number | string;
  };

const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ name, size, color, width, height, className, ...rest }, ref) => {
    const IconComponent = iconMap[name];

    if (!IconComponent) {
      if (import.meta.env.DEV) {
        console.warn(`[Icon] Unknown icon name: "${name}"`);
      }
      return null;
    }

    return (
      <IconComponent
        ref={ref}
        fill="currentColor"
        width={width ?? (size ? undefined : 20)}
        height={height ?? (size ? undefined : 20)}
        className={cn(
          iconVariants({
            size: width || height ? undefined : size,
            color,
          }),
          className
        )}
        {...rest}
      />
    );
  }
);

Icon.displayName = "Icon";

export { Icon, type IconName, type IconProps };
